// Lib
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';

// Utils
import { navigateToUrl, navigateToBoard } from '../../reducers/navigationActions';
import { RerouteDeepLinkPlugin } from '../../../capacitor_plugins/rerouteDeepLink';
import { CAPACITOR_REROUTE_DEEP_LINK_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import { withPayloadLogging } from '../capacitorPayloadLogging';

// Selectors
import { getCurrentUserLastViewedBoardId, getCurrentUserRootBoardId } from '../../user/currentUserSelector';
import { getLocalLastNavigationId } from '../../user/navigationHistory/localNavigationHistoryService';

const parseUrl = (url: string): URL | null => {
    try {
        return new URL(url);
    } catch {
        return null;
    }
};

const isMilanoteAppHost = (hostname: string): boolean => {
    return hostname === 'app.milanote.com' || hostname === 'staging.milanote.com';
};

// Check if the URL needs to be hit first and then use the resulting URL (i.e. for redirects)
const shouldResolveUrl = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    const hostname = parsedUrl?.hostname;

    return (
        url.includes('.milanote.com/api/auth/email/verify') || // email verification
        hostname === 'links.milanote.com' || // links in daily digest, notifications, etc
        hostname === 'comms.milanote.com' || // intercom branded links
        url.includes('account.milanote.com/subscription-invite#code=') // claim pro account
    );
};

const isBoardUrl = (url: string): boolean => {
    // The board ID is matched for 14 to 15 characters because there was some issue with element ID creation at some point
    // that caused ID creations with an extra character, which now exists in the database.
    // This regex also matches an optional permission ID ("p") query parameter.
    const boardUrlRegex =
        /^https:\/\/(app|staging)\.milanote\.com\/[a-zA-Z0-9]{14,15}(?:\/.*)?(?:\?p=[a-zA-Z0-9]{11})?\/?$/;
    return boardUrlRegex.test(url);
};

const isRootAppUrl = (url: string): boolean => {
    const parsedUrl = parseUrl(url);
    if (!parsedUrl) return false;
    if (!isMilanoteAppHost(parsedUrl.hostname)) return false;

    // Accept root URLs even when they include query/hash from wrapped links.
    return parsedUrl.pathname === '' || /^\/+$/.test(parsedUrl.pathname);
};

// Check if the URL should just be used as is.
const shouldDirectlyNavigate = (url: string): boolean => {
    return (
        url.includes('/change-password?code=') || // password reset
        isBoardUrl(url)
    );
};

export default function () {
    const dispatch = useDispatch();
    const lastViewedBoard = useSelector(getCurrentUserLastViewedBoardId);
    const rootBoardId = useSelector(getCurrentUserRootBoardId);

    useEffect(() => {
        if (!Capacitor.isPluginAvailable('App')) return;

        const navigateToLastOpenedBoard = () => {
            // Navigate to last opened board when opening the root app URL
            // This mirrors the web app behavior from currentUserActions.js
            const localLastNavigation = getLocalLastNavigationId();

            const boardId = localLastNavigation || lastViewedBoard || rootBoardId;

            if (boardId) {
                dispatch(navigateToBoard({ boardId, resetScroll: true, navigationSource: undefined }));
            }
        };

        const handleResolvedUrl = (resolvedUrl: string) => {
            if (isRootAppUrl(resolvedUrl)) {
                navigateToLastOpenedBoard();
                return;
            }

            dispatch(navigateToUrl(resolvedUrl));
        };

        App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
            const url = event.url;
            if (shouldResolveUrl(url)) {
                if (!Capacitor.isPluginAvailable('CapacitorHttp')) return;
                CapacitorHttp.get({ url }).then((response) => {
                    handleResolvedUrl(response.url);
                });
                return;
            }

            if (isRootAppUrl(url)) {
                navigateToLastOpenedBoard();
                return;
            }

            if (shouldDirectlyNavigate(url)) {
                dispatch(navigateToUrl(url));
                return;
            }

            if (!Capacitor.isPluginAvailable(CAPACITOR_REROUTE_DEEP_LINK_PLUGIN)) return;
            withPayloadLogging(
                `${CAPACITOR_REROUTE_DEEP_LINK_PLUGIN}.openUrlInExternalBrowserWithoutLoopingBackToApp`,
                (payload) => RerouteDeepLinkPlugin.openUrlInExternalBrowserWithoutLoopingBackToApp(payload),
                { url },
            );
        });
    }, [lastViewedBoard, rootBoardId]);
}
